import { LitElement, html, css } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import {
  Chart,
  type ChartConfiguration,
  type ChartType as CJSType,
} from 'chart.js/auto';
import type { DrillDownContext } from '../../utils/drill-down-template';

const PALETTE = [
  '#6200EA',
  '#03DAC6',
  '#FF6B35',
  '#1976D2',
  '#FFC107',
  '#9C27B0',
  '#4CAF50',
  '#F44336',
  '#FF9800',
  '#00BCD4',
  '#795548',
  '#607D8B',
];

type BiaChartType = 'bar' | 'line' | 'pie' | 'doughnut';

interface BiaChartDataset {
  label: string;
  data: number[];
}

interface BiaChartData {
  chartType: BiaChartType;
  labels: string[];
  datasets: BiaChartDataset[];
  /** Optional human-readable title shown above the chart. */
  title?: string;
}

function isValid(d: unknown): d is BiaChartData {
  if (!d || typeof d !== 'object') return false;
  const obj = d as Record<string, unknown>;
  if (!['bar', 'line', 'pie', 'doughnut'].includes(obj.chartType as string)) return false;
  if (!Array.isArray(obj.labels)) return false;
  if (!Array.isArray(obj.datasets) || obj.datasets.length === 0) return false;
  for (const ds of obj.datasets as unknown[]) {
    if (!ds || typeof ds !== 'object') return false;
    const dsObj = ds as Record<string, unknown>;
    if (typeof dsObj.label !== 'string') return false;
    if (!Array.isArray(dsObj.data)) return false;
  }
  return true;
}

@customElement('bia-chart-renderer')
export class BiaChartRenderer extends LitElement {
  static styles = css`
    :host {
      display: block;
      width: 100%;
      margin: 0.4em 0 0.2em;
    }
    .wrap {
      position: relative;
      width: 100%;
      height: 240px;
      background: #ffffff;
      border-radius: 6px;
      padding: 8px;
      box-sizing: border-box;
    }
    .expand-btn {
      position: absolute;
      top: 6px;
      right: 6px;
      width: 26px;
      height: 26px;
      border-radius: 6px;
      background: rgba(255, 255, 255, 0.92);
      border: 1px solid var(--bia-border, #e0e0e0);
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 2;
      color: var(--bia-text, #212121);
      padding: 0;
      transition: background 140ms ease;
    }
    .expand-btn:hover {
      background: #ffffff;
    }
    .expand-btn svg {
      width: 14px;
      height: 14px;
      fill: none;
      stroke: currentColor;
      stroke-width: 1.8;
      stroke-linecap: round;
      stroke-linejoin: round;
    }
    .empty {
      font-style: italic;
      color: var(--bia-text-muted, #757575);
      font-size: 0.85em;
    }

    /* ---------- Modal (top layer via <dialog>) ---------- */
    dialog.modal {
      width: 92vw;
      height: 92vh;
      max-width: 1400px;
      max-height: 900px;
      padding: 0;
      border: none;
      border-radius: 14px;
      background: #ffffff;
      box-shadow: 0 24px 60px rgba(0, 0, 0, 0.45);
      overflow: hidden;
      /* Reset host CSS that might bleed in via inheritance */
      color: #0f0f0e;
      font-family: var(--bia-font-family, sans-serif);
    }
    dialog.modal::backdrop {
      background: rgba(0, 0, 0, 0.55);
      backdrop-filter: blur(2px);
    }
    .modal-body {
      position: relative;
      width: 100%;
      height: 100%;
      padding: 32px 24px 24px;
      box-sizing: border-box;
      display: flex;
      flex-direction: column;
    }
    .modal-canvas-wrap {
      flex: 1 1 0;
      min-height: 0;
      position: relative;
    }
    .close-btn {
      position: absolute;
      top: 12px;
      right: 12px;
      width: 32px;
      height: 32px;
      border-radius: 8px;
      background: #ffffff;
      border: 1px solid #e0e0e0;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 2;
      color: #0f0f0e;
      padding: 0;
      transition: background 140ms ease;
    }
    .close-btn:hover {
      background: #f3f4f6;
    }
    .close-btn svg {
      width: 16px;
      height: 16px;
      fill: none;
      stroke: currentColor;
      stroke-width: 2;
      stroke-linecap: round;
      stroke-linejoin: round;
    }
  `;

  @property({ type: Object }) data: unknown = null;

  @state() private isOpen = false;

  @query('canvas.inline') private inlineCanvas?: HTMLCanvasElement;
  @query('canvas.modal-canvas') private modalCanvas?: HTMLCanvasElement;
  @query('dialog.modal') private dialogEl?: HTMLDialogElement;

  private inlineChart?: Chart;
  private modalChart?: Chart;

  updated(changed: Map<string, unknown>): void {
    if (changed.has('data')) {
      this.renderInlineChart();
    }
    if (changed.has('isOpen')) {
      if (this.isOpen) {
        this.openModal();
      } else {
        this.closeModal();
      }
    }
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.inlineChart?.destroy();
    this.inlineChart = undefined;
    this.modalChart?.destroy();
    this.modalChart = undefined;
    if (this.dialogEl?.open) this.dialogEl.close();
  }

  /**
   * Returns a data URL of the inline chart in the requested format. Both
   * PNG and JPG get a white background painted under the chart.
   */
  exportImage(format: 'png' | 'jpg' = 'png'): string | null {
    const chart = this.modalChart ?? this.inlineChart;
    const canvas = this.modalChart?.canvas ?? this.inlineCanvas;
    if (!chart || !canvas) return null;
    const out = document.createElement('canvas');
    out.width = canvas.width;
    out.height = canvas.height;
    const ctx = out.getContext('2d');
    if (!ctx) return null;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, out.width, out.height);
    ctx.drawImage(canvas, 0, 0);
    return format === 'png'
      ? out.toDataURL('image/png')
      : out.toDataURL('image/jpeg', 0.92);
  }

  hasChart(): boolean {
    return this.inlineChart != null;
  }

  private toggleExpanded(): void {
    this.isOpen = !this.isOpen;
  }

  private renderInlineChart(): void {
    if (this.inlineChart) {
      this.inlineChart.destroy();
      this.inlineChart = undefined;
    }
    if (!isValid(this.data) || !this.inlineCanvas) return;
    this.inlineChart = new Chart(this.inlineCanvas, this.buildConfig(this.data));
  }

  private async openModal(): Promise<void> {
    if (!this.dialogEl) return;
    if (!this.dialogEl.open) {
      this.dialogEl.showModal();
    }
    // Wait for the dialog to be in the top layer + canvas to size up
    await this.updateComplete;
    requestAnimationFrame(() => {
      if (!isValid(this.data) || !this.modalCanvas) return;
      if (this.modalChart) {
        this.modalChart.destroy();
      }
      this.modalChart = new Chart(this.modalCanvas, this.buildConfig(this.data));
    });
  }

  private closeModal(): void {
    if (this.modalChart) {
      this.modalChart.destroy();
      this.modalChart = undefined;
    }
    if (this.dialogEl?.open) {
      this.dialogEl.close();
    }
  }

  private onDialogClose = (): void => {
    // Fired by ESC key or .close() — keep state in sync
    if (this.isOpen) {
      this.isOpen = false;
    }
  };

  private buildConfig(data: BiaChartData): ChartConfiguration {
    const isPieLike = data.chartType === 'pie' || data.chartType === 'doughnut';

    const datasets = data.datasets.map((ds, i) => ({
      label: ds.label,
      data: ds.data,
      backgroundColor: isPieLike
        ? data.labels.map((_, idx) => PALETTE[idx % PALETTE.length])
        : PALETTE[i % PALETTE.length],
      borderColor: isPieLike ? '#ffffff' : PALETTE[i % PALETTE.length],
      borderWidth: isPieLike ? 1 : 1.5,
      tension: data.chartType === 'line' ? 0.3 : 0,
    }));

    const showLegend = isPieLike || data.datasets.length > 1;
    const TEXT_COLOR = '#212121';
    const TITLE_COLOR = '#0f0f0e';
    const GRID_COLOR = 'rgba(0,0,0,0.08)';
    const hasTitle = !!data.title?.trim();

    return {
      type: data.chartType as CJSType,
      data: { labels: data.labels, datasets },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        color: TEXT_COLOR,
        onClick: (_evt, elements, chart) => {
          // Drill-down is only meaningful for line/bar. Pie/doughnut
          // slices ARE the decomposition — clicking on one would
          // produce a trivially circular request.
          if (data.chartType !== 'bar' && data.chartType !== 'line') return;
          if (!elements || elements.length === 0) return;
          const el = elements[0];
          if (!el) return;
          const datasetIndex = el.datasetIndex;
          const index = el.index;
          const ds = chart.data.datasets[datasetIndex];
          const labels = chart.data.labels as string[] | undefined;
          if (!ds || !labels) return;
          const pointLabel = labels[index];
          const rawValue = (ds.data as number[])[index];
          if (typeof rawValue !== 'number' || !Number.isFinite(rawValue)) return;
          const chartTitle =
            (chart.options.plugins?.title?.text as string | undefined) ?? '';
          const detail = {
            chartTitle,
            seriesLabel: (ds.label as string | undefined) ?? '',
            pointLabel: pointLabel ?? '',
            value: rawValue,
          };
          this.dispatchEvent(
            new CustomEvent<DrillDownContext>('chartpointclick', {
              detail,
              bubbles: true,
              composed: true,
            }),
          );
          // If the click happened inside the maximized dialog, close it so
          // the user sees the prefilled input without another interaction.
          if (this.isOpen) {
            this.isOpen = false;
          }
        },
        layout: {
          padding: { top: hasTitle ? 4 : 0, bottom: 0, left: 0, right: 0 },
        },
        plugins: {
          title: {
            display: hasTitle,
            text: data.title?.trim() ?? '',
            color: TITLE_COLOR,
            font: {
              size: 13,
              weight: 600,
              family:
                "'DM Sans', -apple-system, BlinkMacSystemFont, system-ui, sans-serif",
            },
            padding: { top: 0, bottom: 12 },
            align: 'start',
          },
          legend: {
            display: showLegend,
            position: isPieLike ? 'right' : 'top',
            labels: { font: { size: 11 }, boxWidth: 10, color: TEXT_COLOR },
          },
          tooltip: {
            callbacks: {
              label: (ctx) => {
                const parsed = ctx.parsed as number | { y: number } | { x: number; y: number };
                const v =
                  typeof parsed === 'number'
                    ? parsed
                    : (parsed as { y: number }).y;
                return `${ctx.dataset.label}: ${formatNumber(v)}`;
              },
            },
          },
        },
        scales: isPieLike
          ? undefined
          : {
              x: {
                ticks: { font: { size: 10 }, color: TEXT_COLOR },
                grid: { color: GRID_COLOR },
              },
              y: {
                beginAtZero: true,
                ticks: {
                  font: { size: 10 },
                  color: TEXT_COLOR,
                  callback: (v) => formatNumber(Number(v)),
                },
                grid: { color: GRID_COLOR },
              },
            },
      },
    };
  }

  render() {
    if (!isValid(this.data)) {
      return html`<div class="empty">Sem dados de gráfico válidos.</div>`;
    }
    const expandIcon = html`<svg viewBox="0 0 24 24"><path d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5"/></svg>`;
    const closeIcon = html`<svg viewBox="0 0 24 24"><path d="M6 6l12 12M18 6L6 18"/></svg>`;
    return html`
      <div class="wrap">
        <button
          class="expand-btn"
          @click=${this.toggleExpanded}
          title="Maximizar gráfico"
          aria-label="Maximizar gráfico"
        >
          ${expandIcon}
        </button>
        <canvas class="inline"></canvas>
      </div>
      <dialog class="modal" @close=${this.onDialogClose}>
        <div class="modal-body">
          <button
            class="close-btn"
            @click=${this.toggleExpanded}
            title="Fechar (Esc)"
            aria-label="Fechar"
          >
            ${closeIcon}
          </button>
          <div class="modal-canvas-wrap">
            <canvas class="modal-canvas"></canvas>
          </div>
        </div>
      </dialog>
    `;
  }
}

function formatNumber(value: number): string {
  if (!Number.isFinite(value)) return '—';
  if (Math.abs(value) >= 1000) {
    return value.toLocaleString('pt-BR', { maximumFractionDigits: 2 });
  }
  return value.toLocaleString('pt-BR', { maximumFractionDigits: 2 });
}
